import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Check, X } from 'lucide-react'
import type { ObjectVersion } from '@/types/entities'
import { useRetrieveVersion } from '../hooks'
import { useToast } from '@/hooks/use-toast'
import { formatBytes } from '../utils/formatSize'

interface RetrieveVersionDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    version: ObjectVersion | null
    objectID: string | null
}

export function RetrieveVersionDialog({
    open,
    onOpenChange,
    version,
    objectID,
}: RetrieveVersionDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const retrieveMutation = useRetrieveVersion()
    const [confirming, setConfirming] = useState(false)
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)
    const [retrievedVersion, setRetrievedVersion] = useState<string>('')

    if (!version || !objectID) {
        return null
    }

    const isRedFlagged = version.objectVersionStatus === 2
    const showWarning = isRedFlagged && !confirming

    const handleConfirm = async () => {
        if (showWarning) {
            setConfirming(true)
            return
        }

        // Call the retrieve mutation
        try {
            const result = await retrieveMutation.mutateAsync({
                objectID,
                versionID: version.versionID,
                recoverRedObjects: isRedFlagged ? 'Yes' : 'No',
            })

            if (result.success) {
                const versionText = `${version.objectPrimary} ${version.objectSecondary}`
                setRetrievedVersion(versionText)
                setShowSuccessDialog(true)
                setConfirming(false)
            } else {
                toast({
                    title: t('objects.versions.retrieveError'),
                    description: t('objects.versions.retrieveErrorMessage'),
                    variant: 'destructive',
                })
            }
        } catch (error) {
            toast({
                title: t('objects.versions.retrieveError'),
                description: t('objects.versions.retrieveErrorMessage'),
                variant: 'destructive',
            })
        } finally {
            setConfirming(false)
        }
    }

    const handleCancel = () => {
        setConfirming(false)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[900px]">
                <DialogHeader>
                    <DialogTitle>{t('objects.versions.retrieveVersion')}</DialogTitle>
                    <DialogDescription>
                        {t('objects.versions.retrieveVersionDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Object ID at the top */}
                    <div className="pb-2">
                        <div className="text-sm">
                            <span className="font-semibold text-muted-foreground">
                                {t('objects.objectID')}:
                            </span>
                            <span className="ml-2 font-medium text-lg">{objectID || '-'}</span>
                        </div>
                    </div>

                    {/* Version Details */}
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectName')}:
                                </span>
                                <span className="ml-2 break-words">{version.objectName || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectPrimary')}:
                                </span>
                                <span className="ml-2">{version.objectPrimary || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectSecondary')}:
                                </span>
                                <span className="ml-2">{version.objectSecondary || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectSize')}:
                                </span>
                                <span className="ml-2">
                                    {version.objectSize !== null && version.objectSize !== undefined
                                        ? formatBytes(version.objectSize)
                                        : '-'}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.status')}:
                                </span>
                                <span className="ml-2 flex items-center">
                                    {version.objectVersionStatus != null ? (
                                        <AnalyticsStatusIndicator status={version.objectVersionStatus} size="h-4 w-4" />
                                    ) : (
                                        <span>-</span>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.vaultDatetime')}:
                                </span>
                                <span className="ml-2">
                                    {version.vaultDatetime
                                        ? new Date(version.vaultDatetime).toLocaleString()
                                        : '-'}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.vaultLocation')}:
                                </span>
                                <span className="ml-2">{version.vaultLocation || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectLocation')}:
                                </span>
                                <span className="ml-2">{version.objectLocation || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.vaultName')}:
                                </span>
                                <span className="ml-2">{version.vaultName || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.vaultID')}:
                                </span>
                                <span className="ml-2">{version.vaultID || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.anomaly')}:
                                </span>
                                <span className="ml-2 flex items-center gap-1">
                                    {version.anomaly && version.anomaly.toLowerCase() === 'yes' ? (
                                        <>
                                            <X className="h-3 w-3 text-red-500" />
                                            <span>Yes</span>
                                        </>
                                    ) : (
                                        <>
                                            <Check className="h-3 w-3 text-green-500" />
                                            <span>No</span>
                                        </>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.versionEncryption')}:
                                </span>
                                <span className="ml-2 flex items-center gap-1">
                                    {version.versionEncryption === 1 || version.versionEncryption === '1' ? (
                                        <>
                                            <Check className="h-3 w-3 text-green-500" />
                                            <span>Yes</span>
                                        </>
                                    ) : (
                                        <>
                                            <X className="h-3 w-3 text-muted-foreground" />
                                            <span>No</span>
                                        </>
                                    )}
                                </span>
                            </div>
                            {version.analyticsText && (
                                <div className="col-span-2">
                                    <span className="font-medium text-muted-foreground">
                                        {t('objects.versions.analytics')}:
                                    </span>
                                    <span className="ml-2">{version.analyticsText}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Warning for Red Flagged Versions */}
                    {showWarning && (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                {t('objects.versions.redFlaggedWarning')}
                            </AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel} disabled={retrieveMutation.isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={retrieveMutation.isPending}
                        variant={isRedFlagged ? 'destructive' : 'default'}
                    >
                        {retrieveMutation.isPending
                            ? t('common.processing')
                            : showWarning
                              ? t('common.yes')
                              : t('objects.versions.confirmRetrieve')}
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Success Dialog */}
            <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.versions.retrieveSuccess')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.versions.retrieveSuccessMessage', {
                                version: retrievedVersion,
                            })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground">
                            {t('objects.versions.retrieveNeedsApproval')}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                setShowSuccessDialog(false)
                                onOpenChange(false)
                            }}
                        >
                            {t('common.yes')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    )
}
